import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { orbitApiFetch } from '../../orbit-api/orbitApiClient';
import { useAuth } from '../../auth/AuthProvider';
import { useBrand } from '../../brand/BrandProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import {
  ticketsListFixture,
  ticketDetailFixture,
  uploadAttachmentFixture,
  attachmentUrlFixture,
} from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/orbit-tickets.md
 * Backend: orbit.api.valitype.net (Postgres-Stack, eigener Service —
 * nicht Nexus, nicht CORE).
 *
 * Operator-Bearer + Brand-Origin werden pro Call gesetzt; der BFF-Proxy
 * (/proxy/orbit-api/*) reicht beide unverändert durch.
 */

function buildHeaders(token, brandOrigin, extra = {}) {
  const headers = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (brandOrigin) headers['Brand-Origin'] = brandOrigin;
  return headers;
}

/** GET /v1/tickets — cursor-paginated */
export function useTicketsList({
  status = 'open,pending',
  priority = '',
  assignedTo = '',
  customerUserId = '',
  search = '',
  cursor = '',
  limit = 25,
  enabled = true,
} = {}) {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: [
      'orbit-tickets',
      { status, priority, assignedTo, customerUserId, search, cursor, limit, brandOrigin },
      fixturesMode ? 'fixture' : 'live',
    ],
    enabled: enabled && !!brandOrigin,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return ticketsListFixture({ status, priority, assignedTo, search, cursor });
      }
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (priority) qs.set('priority', priority);
      if (assignedTo) qs.set('assignedTo', assignedTo);
      if (customerUserId) qs.set('customerUserId', String(customerUserId));
      if (search) qs.set('search', search);
      if (cursor) qs.set('cursor', cursor);
      if (limit) qs.set('limit', String(limit));
      return orbitApiFetch(`/v1/tickets?${qs.toString()}`, {
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      });
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** GET /v1/tickets/:id — Detail + Replies + Audit */
export function useTicketDetail(idOrNumber) {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: [
      'orbit-ticket-detail',
      String(idOrNumber ?? ''),
      brandOrigin,
      fixturesMode ? 'fixture' : 'live',
    ],
    enabled: !!idOrNumber && !!brandOrigin,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return ticketDetailFixture(idOrNumber);
      }
      return orbitApiFetch(`/v1/tickets/${encodeURIComponent(idOrNumber)}`, {
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      });
    },
  });
}

/** POST /v1/tickets */
export function useCreateTicket() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, payload) =>
      orbitApiFetch('/v1/tickets', {
        method: 'POST',
        body: payload,
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      }),
    fixture: (payload) => ({
      ticket: {
        id: Date.now(),
        ticketNumber: `VKY-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`,
        subject: payload.subject,
        status: 'open',
        priority: payload.priority ?? 'normal',
        channel: payload.channel ?? 'manual',
      },
    }),
    invalidate: ['orbit-tickets'],
    successMessage: (res) =>
      `Ticket ${res?.ticket?.ticketNumber ?? ''} angelegt.`,
  });
}

/** POST /v1/tickets/:id/replies — kind: operator_reply | operator_note */
export function useTicketReply() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, kind = 'operator_reply', body, attachmentIds = [] }) =>
      orbitApiFetch(`/v1/tickets/${encodeURIComponent(id)}/replies`, {
        method: 'POST',
        body: { kind, body, attachmentIds },
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      }),
    fixture: ({ kind, body }) => ({
      reply: { id: Date.now(), kind, body, createdAt: new Date().toISOString() },
    }),
    invalidate: ['orbit-ticket-detail', 'orbit-tickets'],
    successMessage: (_res, { kind }) =>
      kind === 'operator_note' ? 'Note hinzugefügt.' : 'Reply gesendet.',
  });
}

/** POST /v1/tickets/:id/notes — Sugar für operator_note */
export function useTicketNote() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, body }) =>
      orbitApiFetch(`/v1/tickets/${encodeURIComponent(id)}/notes`, {
        method: 'POST',
        body: { body },
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      }),
    fixture: ({ body }) => ({
      reply: { id: Date.now(), kind: 'operator_note', body, createdAt: new Date().toISOString() },
    }),
    invalidate: ['orbit-ticket-detail', 'orbit-tickets'],
    successMessage: 'Interne Notiz hinzugefügt.',
  });
}

/** POST /v1/tickets/:id/status — { status, reason? } */
export function useTicketStatus() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, status, reason }) =>
      orbitApiFetch(`/v1/tickets/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: reason ? { status, reason } : { status },
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      }),
    fixture: ({ status }) => ({ ok: true, status }),
    invalidate: ['orbit-ticket-detail', 'orbit-tickets'],
    successMessage: (_res, { status }) => `Status → ${status}.`,
  });
}

/** POST /v1/tickets/:id/assign — { operatorUserId } (null = unassign) */
export function useTicketAssign() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, operatorUserId }) =>
      orbitApiFetch(`/v1/tickets/${encodeURIComponent(id)}/assign`, {
        method: 'POST',
        body: { operatorUserId },
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      }),
    fixture: ({ operatorUserId }) => ({ ok: true, operatorUserId }),
    invalidate: ['orbit-ticket-detail', 'orbit-tickets'],
    successMessage: (_res, { operatorUserId }) =>
      operatorUserId == null ? 'Assignee entfernt.' : 'Assignee gesetzt.',
  });
}

/**
 * POST /v1/tickets/:id/attachments — multipart, field "file".
 * Content-Type wird NICHT gesetzt — der Browser hängt die boundary an.
 */
export function useAttachmentUpload() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, file }) => {
      const fd = new FormData();
      fd.append('file', file);
      return orbitApiFetch(`/v1/tickets/${encodeURIComponent(id)}/attachments`, {
        method: 'POST',
        body: fd,
        headers: buildHeaders(auth.getAccessToken(), brandOrigin),
      });
    },
    fixture: ({ file }) => uploadAttachmentFixture(file),
    invalidate: ['orbit-ticket-detail'],
    successMessage: (res) =>
      res?.attachment?.filename
        ? `${res.attachment.filename} hochgeladen.`
        : 'Attachment hochgeladen.',
  });
}

/** GET /v1/tickets/:id/attachments/:attId/url — Presigned-Download (15 min) */
export function useAttachmentUrl() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  const fixturesMode = useFixturesMode();
  return async ({ id, attId }) => {
    if (fixturesMode) {
      await fixtureDelay();
      return attachmentUrlFixture(id, attId);
    }
    return orbitApiFetch(
      `/v1/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}/url`,
      { headers: buildHeaders(auth.getAccessToken(), brandOrigin) },
    );
  };
}

/** DELETE /v1/tickets/:id/attachments/:attId */
export function useAttachmentDelete() {
  const auth = useAuth();
  const brand = useBrand();
  const brandOrigin = brand.currentBrandOrigin;
  return useApiMutation({
    live: (_fetch, { id, attId }) =>
      orbitApiFetch(
        `/v1/tickets/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`,
        {
          method: 'DELETE',
          headers: buildHeaders(auth.getAccessToken(), brandOrigin),
        },
      ),
    fixture: () => ({ ok: true }),
    invalidate: ['orbit-ticket-detail'],
    successMessage: 'Attachment entfernt.',
  });
}
